import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, Moon, Laptop } from "lucide-react";
import { getEquipamentosTicketMedio, putEquipamentoTicketMedio, getRoleFromToken } from "../../api";
import SideBarNavGestaoOrcamentaria from "../../componets/SideBarNavGestaoOrcamentaria";

const palette = (isLight) => ({
  bg: isLight ? "#F8F7FC" : "#0F0A1E",
  card: isLight ? "#FFFFFF" : "#1A1030",
  border: isLight ? "#E5E0F0" : "#2E2050",
  text: isLight ? "#1A1030" : "#EDE9FA",
  muted: isLight ? "#7C6FA0" : "#9585C0",
  brand: "#7C3AED",
  danger: "#EF4444",
  subcategoriaBg: isLight ? "#F5F0FC" : "#1E1435",
});

const headerBtnStyle = {
  display: "flex", alignItems: "center", gap: 6,
  background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)",
  borderRadius: 8, padding: "7px 12px", cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
};

function inputStyle(C) {
  return {
    background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
    padding: "9px 12px", fontSize: 13, color: C.text, fontFamily: "inherit", outline: "none", width: "100%",
  };
}

function btnStyle(color) {
  return {
    display: "flex", alignItems: "center", gap: 6, background: color, border: "none", color: "#fff",
    borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700, fontFamily: "inherit", cursor: "pointer",
  };
}

// Premissas · Equipamentos e Materiais: ticket médio travado, ÚNICO/global, lista fixa de 5
// itens (Fone, Mouse, Teclado, Notebook, Outros) — mesmo mecanismo de Premissas · Viagens
// (Backoffice), mas sem vínculo com o plano de contas: é uma premissa isolada, ainda não
// consumida por nenhum lançamento de custo (fora de escopo por ora).
export default function PremissasEquipamentosView() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const navigate = useNavigate();
  const isAdmin = getRoleFromToken() === "admin";

  const [equipamentos, setEquipamentos] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [salvandoCodigo, setSalvandoCodigo] = useState(null);

  useEffect(() => {
    getEquipamentosTicketMedio()
      .then(setEquipamentos)
      .catch((e) => setErro(e.message))
      .finally(() => setCarregando(false));
  }, []);

  async function salvarTicket(codigo, valor) {
    setSalvandoCodigo(codigo);
    setErro("");
    try {
      const atualizado = await putEquipamentoTicketMedio(codigo, Number(valor));
      setEquipamentos((lista) => lista.map((e) => (e.codigo === codigo ? atualizado : e)));
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvandoCodigo(null);
    }
  }

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <SideBarNavGestaoOrcamentaria isLight={isLight} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)", color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.8, marginLeft: 8 }}>· Premissas · Equipamentos e Materiais</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={() => setIsLight((l) => !l)} style={headerBtnStyle}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />} {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button onClick={() => navigate("/inicio")} style={headerBtnStyle}>Menu Principal</button>
          </div>
        </header>

        <main style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: C.text }}>Premissas · Equipamentos e Materiais</h1>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: C.muted }}>
              Ticket médio global de cada item — lista fixa, só o valor é editável.
              {!isAdmin && " Somente admin edita — os demais perfis visualizam."}
            </p>
          </div>

          {erro && <p style={{ margin: 0, fontSize: 12, color: C.danger }}>{erro}</p>}

          <section style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <Laptop size={15} color={C.brand} />
              <h2 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.text }}>Equipamentos e Materiais</h2>
            </div>

            {carregando ? (
              <p style={{ fontSize: 12, color: C.muted }}>Carregando…</p>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
                {equipamentos.map((e) => (
                  <CartaoEquipamento
                    key={e.codigo} C={C} equipamento={e} isAdmin={isAdmin}
                    salvando={salvandoCodigo === e.codigo}
                    onSalvar={(v) => salvarTicket(e.codigo, v)}
                  />
                ))}
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
}

function CartaoEquipamento({ C, equipamento, isAdmin, salvando, onSalvar }) {
  const [valor, setValor] = useState(equipamento.ticket_medio);
  useEffect(() => { setValor(equipamento.ticket_medio); }, [equipamento.ticket_medio]);
  const alterado = Number(valor) !== Number(equipamento.ticket_medio);

  return (
    <div style={{ background: C.subcategoriaBg, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: C.muted }}>{equipamento.nome}</label>
      <div style={{ display: "flex", gap: 4 }}>
        <input
          type="number" value={valor} disabled={!isAdmin || salvando}
          onChange={(e) => setValor(e.target.value)}
          style={{ ...inputStyle(C), padding: "6px 8px", opacity: isAdmin ? 1 : 0.6 }}
        />
        {isAdmin && alterado && (
          <button onClick={() => onSalvar(valor)} disabled={salvando} style={{ ...btnStyle(C.brand), padding: "6px 10px", fontSize: 11, whiteSpace: "nowrap" }}>
            {salvando ? "..." : "Salvar"}
          </button>
        )}
      </div>
    </div>
  );
}
